import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFilePdf, faPenToSquare, faTrash, faPlus, faListCheck, faClock, faClipboardList, faUserShield, faCoins, faWrench, faUser, faCalendarAlt, faClock as faTimeIcon } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, canEditWorkOrder } from '../config/api';

// The setIsAuthenticated function is now passed as a prop
const DashboardPage = ({ setIsAuthenticated }) => {
    // State to hold the fetched work orders, loading status, and any errors
    const [workOrders, setWorkOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState(() => {
        // ako je serviser (user), podrazumevano prikaži "Moji", u suprotnom "Svi"
        try {
            const u = JSON.parse(localStorage.getItem('user') || 'null');
            return u && u.role === 'user' ? 'mine' : 'all';
        } catch {
            return 'all';
        }
    }); // all | completed | pending | mine
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const [editableOrders, setEditableOrders] = useState(new Map());
    
    // State for live date and time
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    
    // State for welcome message
    const [welcomeMessage, setWelcomeMessage] = useState(null);
    const [showWelcome, setShowWelcome] = useState(false);
    const [welcomeProgress, setWelcomeProgress] = useState(100);
    const [isWelcomeFading, setIsWelcomeFading] = useState(false);

    const currentUser = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
    }, []);

    // useEffect hook to fetch work orders when the component mounts
    useEffect(() => {
        const fetchWorkOrders = async () => {
            try {
                // Get the JWT token from local storage
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('No authentication token found.');
                }

                // Make the fetch request, including the token in the headers
                const response = await fetch(API_CONFIG.ENDPOINTS.NALOZI, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch work orders.');
                }

                const data = await response.json();
                setWorkOrders(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkOrders();
    }, []); // Empty dependency array means this runs only once on mount

    // Check edit permissions for work orders
    useEffect(() => {
        const checkEditPermissions = async () => {
            if (workOrders.length === 0) return;
            
            const permissions = new Map();
            await Promise.all(
                workOrders.map(async (nalog) => {
                    const canEdit = await canEditWorkOrder(nalog.user_id || nalog.User?.id);
                    permissions.set(nalog.id_nalog, canEdit);
                })
            );
            setEditableOrders(permissions);
        };
        
        checkEditPermissions();
    }, [workOrders]);
    
    // Check for welcome message on component mount
    useEffect(() => {
        try {
            const welcomePending = sessionStorage.getItem('welcomePending');
            const welcomeName = sessionStorage.getItem('welcomeName');
            if (welcomePending && welcomeName) {
                setWelcomeMessage(welcomeName);
                setShowWelcome(true);
                setWelcomeProgress(100);
                setIsWelcomeFading(false);
                
                // Clear from sessionStorage immediately to prevent showing again
                sessionStorage.removeItem('welcomePending');
                sessionStorage.removeItem('welcomeName');
                
                // Progress bar animation (countdown)
                const progressInterval = setInterval(() => {
                    setWelcomeProgress(prev => {
                        const newProgress = prev - 1; // Decrease by 1% every 100ms (10 seconds total)
                        if (newProgress <= 0) {
                            clearInterval(progressInterval);
                            return 0;
                        }
                        return newProgress;
                    });
                }, 100);
                
                // Start fade out at 8 seconds
                const fadeTimer = setTimeout(() => {
                    setIsWelcomeFading(true);
                }, 8000);
                
                // Complete hide after 10 seconds
                const hideTimer = setTimeout(() => {
                    setShowWelcome(false);
                    setIsWelcomeFading(false);
                }, 10000);
                
                return () => {
                    clearInterval(progressInterval);
                    clearTimeout(fadeTimer);
                    clearTimeout(hideTimer);
                };
            }
        } catch (error) {
            console.log('Welcome message check failed:', error);
        }
    }, []);
    
    // useEffect hook to update date and time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 1000);
        
        // Cleanup interval on component unmount
        return () => clearInterval(timer);
    }, []);
    
    // Format date and time for display
    const formatDate = (date) => {
        return date.toLocaleDateString('sr-Latn-RS', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    
    const formatTime = (date) => {
        return date.toLocaleTimeString('sr-Latn-RS', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Function to handle the PDF report download
    const handleDownloadReport = async (nalogId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/report/${nalogId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to download report.');
            }

            // The response is a blob (binary data), so we create a temporary URL for it
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `nalog-za-rad-${nalogId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            // Using alert() here as a simple way to show a message to the user
            alert(`Error downloading report: ${err.message}`);
        }
    };

    // Removed local logout; use Navbar logout instead

    const filteredWorkOrders = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const byQuery = (q
            ? workOrders.filter((nalog) => {
            const id = String(nalog.id_nalog || '').toLowerCase();
            const datum = String(nalog.datum || '').toLowerCase();
            const ustanova = String(nalog.Ustanove?.naziv_ustanove || '').toLowerCase();
            const opis = String(nalog.opis_kvara_intervencija || '').toLowerCase();
                return id.includes(q) || datum.includes(q) || ustanova.includes(q) || opis.includes(q);
            })
            : workOrders);
        const byStatus = byQuery.filter((n) => {
            if (filterStatus === 'completed') return n.checkmark === 'Yes';
            if (filterStatus === 'pending') return n.checkmark !== 'Yes';
            if (filterStatus === 'mine') {
                const uid = currentUser?.id;
                const ownerId = n.user_id ?? n.User?.id;
                return uid && String(ownerId) === String(uid);
            }
            return true;
        });
        return byStatus;
    }, [workOrders, searchQuery, filterStatus, currentUser]);

    // KPIs
    const kpis = useMemo(() => {
        const total = workOrders.length;
        const completed = workOrders.filter(n => n.checkmark === 'Yes').length;
        const pending = total - completed;
        const institutions = new Set(workOrders.map(n => n.Ustanove?.id_ustanova)).size;
        
        // Calculate "My Orders" - orders for the logged-in user
        const myOrders = currentUser ? workOrders.filter(n => {
            const ownerId = n.user_id ?? n.User?.id;
            return String(ownerId) === String(currentUser.id);
        }).length : 0;
        
        return { total, completed, pending, institutions, myOrders };
    }, [workOrders, currentUser]);

    const handleDelete = async (nalogId) => {
        if (!window.confirm('Jeste li sigurni da želite obrisati ovaj nalog? Ova akcija će vratiti utrošene dijelove na lager.')) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/${nalogId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok && res.status !== 204) {
                throw new Error('Brisanje nije uspjelo.');
            }
            // Optimistično ažuriraj listu
            setWorkOrders((prev) => prev.filter(n => n.id_nalog !== nalogId));
        } catch (err) {
            alert(err.message);
        }
    };

    const totalPages = Math.max(1, Math.ceil(filteredWorkOrders.length / itemsPerPage));
    const page = Math.min(currentPage, totalPages);
    const startIndex = (page - 1) * itemsPerPage;
    const visibleWorkOrders = filteredWorkOrders.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-gray-600">Učitavanje...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-3xl mx-auto p-4">
                <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Welcome Message - Controlled by state with progress bar and fade effect */}
            {showWelcome && welcomeMessage && (
                <div className={`bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-4 text-white relative transition-opacity duration-[2000ms] ${
                    isWelcomeFading ? 'opacity-0' : 'opacity-100'
                }`}>
                    <button 
                        onClick={() => {
                            setIsWelcomeFading(true);
                            setTimeout(() => setShowWelcome(false), 2000);
                        }}
                        className="absolute top-2 right-2 text-white/70 hover:text-white text-lg leading-none transition-colors"
                        title="Zatvori poruku"
                    >
                        ×
                    </button>
                    <div className="flex items-center gap-3 pr-8">
                        <div className="bg-white/20 p-3 rounded-full">
                            <FontAwesomeIcon icon={faUser} size="lg" />
                        </div>
                        <div className="flex-1">
                            <div className="text-lg font-semibold">
                                Dobrodošli, {welcomeMessage}!
                            </div>
                            <div className="text-sm text-white/90">
                                Sretan rad. Uspješno ste se prijavili u sistem.
                            </div>
                        </div>
                    </div>
                    {/* Progress bar moved to bottom with proper border radius */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20 rounded-b-xl overflow-hidden">
                        <div 
                            className="bg-white h-full transition-all duration-100 ease-linear rounded-b-xl"
                            style={{ width: `${welcomeProgress}%` }}
                        ></div>
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-semibold text-gray-900">Dashboard</h2>
                    <p className="text-sm text-gray-600 mt-1">Pregled radnih naloga i brze akcije.</p>
                </div>
                {currentUser?.role !== 'finance' && (
                    <button
                        onClick={() => navigate('/new-work-order')}
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Novi nalog
                    </button>
                )}
            </div>

            {/* Live Date and Time Card */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg p-4 sm:p-5 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-2 sm:p-3 rounded-full">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-sm sm:text-lg" />
                        </div>
                        <div>
                            <div className="text-xs sm:text-sm text-white/90">Danas je</div>
                            <div className="text-sm sm:text-xl font-semibold leading-tight">{formatDate(currentDateTime)}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-2 sm:p-3 rounded-full">
                            <FontAwesomeIcon icon={faTimeIcon} className="text-sm sm:text-lg" />
                        </div>
                        <div>
                            <div className="text-xs sm:text-sm text-white/90">Trenutno vrijeme</div>
                            <div className="text-lg sm:text-2xl font-bold">{formatTime(currentDateTime)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {currentUser && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Prijavljeni korisnik</div>
                    <div className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        {`${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()}
                        {(() => {
                          const role = currentUser && currentUser.role ? String(currentUser.role || '').toLowerCase() : '';
                          const config = {
                            admin: { label: 'Admin', icon: faUserShield, classes: 'bg-rose-100 text-rose-800' },
                            finance: { label: 'Finansije', icon: faCoins, classes: 'bg-emerald-100 text-emerald-800' },
                            user: { label: 'Serviser', icon: faWrench, classes: 'bg-blue-100 text-blue-800' },
                          };
                          const { label, icon, classes } = config[role] || { label: role || 'User', icon: faUser, classes: 'bg-blue-100 text-blue-800' };
                          const tooltip = role === 'admin'
                            ? 'Admin privilegije: dodavanje, brisanje i uređivanje korisnika'
                            : role === 'finance'
                              ? 'Finance privilegije: finansijska obrada naloga'
                              : 'Serviser privilegije: kreiranje i uređivanje naloga';
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${classes}`} title={tooltip}>
                              <FontAwesomeIcon icon={icon} /> {label}
                            </span>
                          );
                        })()}
                    </div>
                </div>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Ukupno naloga</div>
                        <div className="text-gray-400"><FontAwesomeIcon icon={faClipboardList} /></div>
                    </div>
                    <div className="mt-1 text-3xl font-semibold text-gray-900">{kpis.total}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Obrađeni</div>
                        <div className="text-emerald-500"><FontAwesomeIcon icon={faListCheck} /></div>
                    </div>
                    <div className="mt-1 text-3xl font-semibold text-gray-900">{kpis.completed}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Neobrađeni</div>
                        <div className="text-amber-500"><FontAwesomeIcon icon={faClock} /></div>
                    </div>
                    <div className="mt-1 text-3xl font-semibold text-gray-900">{kpis.pending}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Mojih naloga</div>
                        <div className="text-purple-500"><FontAwesomeIcon icon={faUser} /></div>
                    </div>
                    <div className="mt-1 text-3xl font-semibold text-gray-900">{kpis.myOrders}</div>
                </div>
            </div>

            <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Popis radnih naloga</h3>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <div className="w-full sm:w-80">
                            <input
                                type="text"
                                placeholder="Pretraga (ID, datum, ustanova, opis)"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                        </div>
                        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                            <button onClick={() => { setFilterStatus('mine'); setCurrentPage(1); }} className={`px-3 py-2 text-sm ${filterStatus==='mine' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Moji</button>
                            <button onClick={() => { setFilterStatus('all'); setCurrentPage(1); }} className={`px-3 py-2 text-sm ${filterStatus==='all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Svi</button>
                            <button onClick={() => { setFilterStatus('completed'); setCurrentPage(1); }} className={`px-3 py-2 text-sm ${filterStatus==='completed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Obrađeni</button>
                            <button onClick={() => { setFilterStatus('pending'); setCurrentPage(1); }} className={`px-3 py-2 text-sm ${filterStatus==='pending' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Neobrađeni</button>
                        </div>
                    </div>
                    {currentUser?.role !== 'finance' && (
                        <button
                            onClick={() => navigate('/new-work-order')}
                            className="sm:hidden inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Novi
                        </button>
                    )}
                </div>
                <div className="">
                    {filteredWorkOrders.length === 0 ? (
                        <div className="p-6 text-center">
                            <div className="text-sm text-gray-600">Nema radnih naloga za prikaz.</div>
                            {currentUser?.role !== 'finance' && (
                                <button onClick={() => navigate('/new-work-order')} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700">
                                    <FontAwesomeIcon icon={faPlus} /> Kreiraj prvi nalog
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                        <div className="overflow-x-auto hidden sm:block">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID naloga</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ustanova</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serviser</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Finansijski obrađeno</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcije</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {visibleWorkOrders.map((nalog) => (
                                        <tr key={nalog.id_nalog} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm text-blue-700 cursor-pointer" onClick={() => navigate(`/work-orders/${nalog.id_nalog}`)}>
                                                #{nalog.id_nalog}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900">{nalog.datum}</td>
                                            <td className="px-4 py-2 text-sm text-gray-900">{nalog.Ustanove?.naziv_ustanove || '-'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-900">{(nalog.User && (nalog.User.first_name || nalog.User.last_name)) ? `${nalog.User.first_name} ${nalog.User.last_name}` : '-'}</td>
                                            <td className="px-4 py-2 text-sm">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${nalog.checkmark === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                    {nalog.checkmark === 'Yes' ? 'Da' : 'Ne'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-sm">
                                                <div className="flex gap-2 justify-end">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/work-orders/${nalog.id_nalog}`); }}
                                                        className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                                                        title="Pregled"
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDownloadReport(nalog.id_nalog); }}
                                                        className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                                                        title="PDF izvještaj"
                                                    >
                                                        <FontAwesomeIcon icon={faFilePdf} />
                                                    </button>
                                                    {editableOrders.get(nalog.id_nalog) && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/work-orders/${nalog.id_nalog}/edit`); }}
                                                            className="px-3 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                                                            title="Uredi"
                                                        >
                                                            <FontAwesomeIcon icon={faPenToSquare} />
                                                        </button>
                                                    )}
                                                    {editableOrders.get(nalog.id_nalog) && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(nalog.id_nalog); }}
                                                            className="px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                                                            title="Obriši"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {/* Mobile cards */}
                        </div>
                        <div className="sm:hidden p-4 space-y-3">
                            {visibleWorkOrders.map((nalog) => (
                                <div key={nalog.id_nalog} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <button onClick={() => navigate(`/work-orders/${nalog.id_nalog}`)} className="text-blue-700 font-medium">#{nalog.id_nalog}</button>
                                            <div className="text-sm text-gray-600">{nalog.datum}</div>
                                        </div>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${nalog.checkmark === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {nalog.checkmark === 'Yes' ? 'Obrađeno' : 'Neobrađeno'}
                                        </span>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-900">{nalog.Ustanove?.naziv_ustanove || '-'}</div>
                                    <div className="mt-1 text-sm text-gray-600">{(nalog.User && (nalog.User.first_name || nalog.User.last_name)) ? `${nalog.User.first_name} ${nalog.User.last_name}` : '-'}</div>
                                    <div className="mt-3 flex gap-2 justify-end">
                                        <button onClick={() => navigate(`/work-orders/${nalog.id_nalog}`)} className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200">
                                            <FontAwesomeIcon icon={faEye} />
                                        </button>
                                        <button onClick={() => handleDownloadReport(nalog.id_nalog)} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700">
                                            <FontAwesomeIcon icon={faFilePdf} />
                                        </button>
                                        {editableOrders.get(nalog.id_nalog) && (
                                            <button onClick={() => navigate(`/work-orders/${nalog.id_nalog}/edit`)} className="px-3 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700">
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </button>
                                        )}
                                        {editableOrders.get(nalog.id_nalog) && (
                                            <button onClick={() => handleDelete(nalog.id_nalog)} className="px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700">
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        </>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-700">
                    <div>
                        Stranica {page} od {totalPages}
                        <span className="ml-2 text-gray-500">({filteredWorkOrders.length === 0 ? 0 : (startIndex + 1)}–{Math.min(startIndex + itemsPerPage, filteredWorkOrders.length)} od {filteredWorkOrders.length})</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className={`px-3 py-2 rounded-md border ${page === 1 ? 'text-gray-400 border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}
                        >
                            Prethodna
                        </button>
                        <button
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className={`px-3 py-2 rounded-md border ${page === totalPages ? 'text-gray-400 border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}
                        >
                            Sljedeća
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;